import { type ReactNode, useEffect } from 'react';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import { useAuthStore } from '@/lib/auth';
import { sanitizeNextPath } from '@/lib/nav-registry';

/**
 * Render-time auth guard. The primary gate is the app layout's `beforeLoad`
 * redirect (see router.tsx) which runs before anything renders; this
 * component only covers the token-cleared-while-mounted case (e.g. sign-out
 * in another tab syncing through the persisted store). Both paths carry the
 * attempted URL as `?next=` so login can return the user to it.
 */
export function ProtectedRoute({ children }: { children: ReactNode }): JSX.Element | null {
  const accessToken = useAuthStore((s) => s.accessToken);
  const navigate = useNavigate();
  const href = useRouterState({ select: (s) => s.location.href });

  useEffect(() => {
    if (!accessToken) {
      const next = sanitizeNextPath(href);
      void navigate({ to: '/login', search: next ? { next } : {} });
    }
  }, [accessToken, navigate, href]);

  if (!accessToken) return null;
  return <>{children}</>;
}
